import { defineComponent } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";

import { enumToMap } from "../../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { Relationships, PhoneTypeEnum, Gender, EthnicityPalliative, RacesPalliative } from "../../../../../../enums/enums";
import { Address } from "../../../../../../resources-vue/vue-models/address";
import { IPhoneNumber } from "../../../../../../resources-vue/vue-interfaces/i-phone-number";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TagsInput from "../../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import PhoneNumber from "../../../../../../resources-vue/vue-custom-elements/PhoneNumber/PhoneNumber.vue";
import AddressForm from "../../../../../../resources-vue/vue-custom-elements/AddressForm/AddressForm.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";


export default defineComponent({
    components: {
        FormSection,
        TagsInput,
        PhoneNumber,
        AddressForm,
        DatePicker
    },


    props: {
        isEditable: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        patientPrimaryAddress: {
            default: null
        },
        contactRolesEnum: {
            type: Array,
            default: []
        },
        relationshipsEnum: {
            type: Array<any>,
            default: []
        },
        contact:{
            type:Object
        }
    },
    validations() {
        return {
            contact: {
                firstName: { required },
                lastName: { required },
                relationship: { required }
            }
        };
    },
    data(vm) {
        return {
            addrSameAsPatientAddr: false,
            selectedContactRoles: [],
            showRelationshipDescription: false,
            phoneNumberOptions: [PhoneTypeEnum.Home, PhoneTypeEnum.Mobile, PhoneTypeEnum.Work],
            Relationships: enumToMap(Relationships),
            Genders: Array.from(Gender.enumWithDescriptions.values()),
            Ethnicity: enumToMap(EthnicityPalliative),
            Races: Array.from(RacesPalliative.enumWithDescriptions.values()),
            selectedEthnicity: [],
            selectedRaces: [],
            isEthnicityDeclined: false,
            isRacesDeclined: false,
            formatDateInput,
            v$: useVuelidate(),
        };
    },
    created() {
        this.init();
    },
    computed: {
        primaryType: {
            get() {
                return this.contact.phoneNumbers[this.primaryNumberIndex(this.contact.phoneNumbers)].type || 1;
            },
            set(value: any) {
                this.contact.phoneNumbers[this.primaryNumberIndex(this.contact.phoneNumbers)].type = value;
            }
        },
        secondaryType: {
            get() {
                return this.contact.phoneNumbers[this.alternativeNumberIndex(this.contact.phoneNumbers)].type || 1;
            },
            set(value: any) {
                return (this.contact.phoneNumbers[this.alternativeNumberIndex(this.contact.phoneNumbers)].type =
                    value);
            }
        },

    },

    unmounted() {
        this.addrSameAsPatientAddr = false;
    },
    watch: {
        addrSameAsPatientAddr(newValue: any) {
            this.addrSameAsPatientAddrChanged(newValue);
        },
        selectedContactRoles() {
            this.selectedContactRolesChanged();
        },
        selectedEthnicity() {
            if (!!this.selectedEthnicity) {
                this.contact.ethnicity = this.selectedEthnicity.map((ethnicity) =>
                    Number(ethnicity.value)
                );
            }
        },
        selectedRaces() {
            if (!!this.selectedRaces) {
                this.contact.race = this.selectedRaces.map((race) => Number(race.value));
            }
        },
    },
    methods: {
        init() {
            if (!!this.contact) {
                this.handleRelationshipSelection();
                if (this.contact.contactRoles) {
                    this.selectedContactRoles = this.contactRolesEnum.filter((role: any) => {
                        return this.contact.contactRoles.some((item: any) => {
                            return role.value == item;
                        });
                    }) as any;
                }

                if (!this.contact.address) {
                    this.contact.address = new Address();
                } else if (this.contact.isUsingPrimaryAddress) {
                    this.addrSameAsPatientAddr = this.contact.isUsingPrimaryAddress;
                }

                if (this.primaryNumberIndex(this.contact.phoneNumbers) < 0) {
                    this.contact.phoneNumbers = this.contact.phoneNumbers.concat({
                        number: "",
                        extension: "",
                        type: 1,
                        isPrimary: true,
                        country: null
                    });
                }

                if (this.alternativeNumberIndex(this.contact.phoneNumbers) < 0) {
                    this.contact.phoneNumbers = this.contact.phoneNumbers.concat({
                        number: "",
                        extension: "",
                        type: 0,
                        isPrimary: false,
                        country: null
                    });
                }

                this.isEthnicityDeclined = this.contact.declineToSpecifyEthnicity;
                this.isRacesDeclined = this.contact.declineToSpecifyRace;
            }
        },
        primaryNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (phoneNumbers) {
                return phoneNumbers.findIndex((e) => e.isPrimary);
            }
            return 0;
        },
        alternativeNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (phoneNumbers) {
                return phoneNumbers.findIndex((e) => !e.isPrimary);
            }
            return 0;
        },
        patientPrimaryAddressChanged() {
            if (!!this.patientPrimaryAddress && !!this.contact && this.contact.isUsingPrimaryAddress) {
                this.setAddressToPrimaryAddress();
            }
        },
        addrSameAsPatientAddrChanged(newValue: boolean) {
            if (newValue === true) {
                this.setAddressToPrimaryAddress();
                this.contact.isUsingPrimaryAddress = true;
            } else {
                this.contact.address = new Address();
                this.contact.isUsingPrimaryAddress = false;
            }
        },
        setAddressToPrimaryAddress() {
            if (!!this.patientPrimaryAddress && !!this.contact) {
                this.contact.address = Object.assign({}, this.patientPrimaryAddress);
            }
        },
        selectedContactRolesChanged() {
            if (this.selectedContactRoles) {
                this.contact.contactRoles = this.selectedContactRoles.map((role) => Number(role.value));
            }
        },
        handleRelationshipSelection() {
            if (!!this.contact.relationship) {
                const isRelationshipOther: boolean = Relationships.enum.Other == this.contact.relationship;
                const isRelationshipOtherRelative: boolean =
                    Relationships.enum.OtherRelative == this.contact.relationship;
                this.showRelationshipDescription = isRelationshipOther || isRelationshipOtherRelative;
            } else {
                this.showRelationshipDescription = false;
            }
        },
        selectedContactRolesUpdated(newValue: any) {
            this.selectedContactRoles = newValue;
        },
        updateAddress(value: any) {
            this.contact.address = value;
        },

        getDate(date: any, name: string) {
            if (name === "dateOfBirth") {
                this.contact.dateOfBirth = date;
            }
        },

        updateSelectedRaces(value: any) {
            this.selectedRaces = value;
        },
        ethnicityDeclined() {
            if (this.isEthnicityDeclined) {
                this.contact.ethnicity = [];
                this.selectedEthnicity = [];
            }
            if (!!this.contact) {
                this.contact.declineToSpecifyEthnicity = this.isEthnicityDeclined;
            }
        },
        racesDeclined() {
            if (this.isRacesDeclined) {
                this.contact.race = [];
                this.selectedRaces = [];
            }
            if (!!this.contact) {
                this.contact.declineToSpecifyRace = this.isRacesDeclined;
            }
        },


    }
});
